import { Link, useParams } from "wouter";
import { useEffect } from "react";
import { ChevronRight } from "lucide-react";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { SeoPageHeader } from "@/components/seo-page-header";
import { Button } from "@/components/ui/button";
import { FAQ_PAGES, FaqSection, getFaqPage } from "@/lib/faq-data";

function Section({ section }: { section: FaqSection }) {
  switch (section.type) {
    case "h2":
      return (
        <h2 className="text-2xl sm:text-3xl font-bold mt-10 mb-4 leading-snug">
          {section.content as string}
        </h2>
      );
    case "h3":
      return (
        <h3 className="text-xl font-semibold mt-8 mb-3 leading-snug">
          {section.content as string}
        </h3>
      );
    case "p":
      return (
        <p className="text-base sm:text-lg leading-relaxed text-foreground/90 mb-4">
          {section.content as string}
        </p>
      );
    case "ul":
      return (
        <ul className="list-disc pl-6 mb-6 space-y-2">
          {(section.content as string[]).map((item, i) => (
            <li key={i} className="text-base sm:text-lg leading-relaxed text-foreground/90">
              {item}
            </li>
          ))}
        </ul>
      );
    case "ol":
    case "numbered":
      return (
        <ol className="list-decimal pl-6 mb-6 space-y-2">
          {(section.content as string[]).map((item, i) => (
            <li key={i} className="text-base sm:text-lg leading-relaxed text-foreground/90">
              {item}
            </li>
          ))}
        </ol>
      );
    case "callout":
      return (
        <div className="border-l-4 border-primary bg-primary/5 rounded-r-xl p-5 my-6">
          <p className="text-base sm:text-lg leading-relaxed text-foreground/90">
            {section.content as string}
          </p>
        </div>
      );
    default:
      return null;
  }
}

export default function FaqPageComponent() {
  const { slug } = useParams<{ slug: string }>();

  const page = getFaqPage(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!page) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Page not found</h1>
          <Link href="/">
            <Button>Go home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const pageUrl = `https://thepropertool.com/faq/${page.slug}/`;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": page.faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://thepropertool.com",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "FAQ",
        "item": "https://thepropertool.com/faq/",
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": page.title,
        "item": pageUrl,
      },
    ],
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "@id": pageUrl,
    "headline": page.title,
    "description": page.metaDescription,
    "datePublished": page.publishedDate,
    "dateModified": page.modifiedDate,
    "author": {
      "@type": "Organization",
      "name": "The Proper Tool",
      "url": "https://thepropertool.com",
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Proper Tool",
      "url": "https://thepropertool.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://thepropertool.com/logo.png",
        "width": 1200,
        "height": 630,
      },
    },
    "url": pageUrl,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": pageUrl,
    },
  };

  const schemas = [faqSchema, breadcrumbSchema, articleSchema, ORGANIZATION_SCHEMA];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title={page.metaTitle}
        description={page.metaDescription}
        canonical={`/faq/${page.slug}/`}
        ogType="article"
        articlePublished={page.publishedDate}
        articleModified={page.modifiedDate}
        schema={schemas}
      />

      <SeoPageHeader />

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
          <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <span>FAQ</span>
          <ChevronRight className="h-4 w-4" />
          <span className="truncate max-w-xs text-foreground">{page.title}</span>
        </div>

        <div className="mb-2">
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">FAQ</span>
        </div>

        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6" data-testid="text-faq-title">
          {page.title}
        </h1>

        <p className="text-xl sm:text-2xl text-muted-foreground leading-relaxed mb-10 pb-8 border-b border-border/60 font-medium">
          {page.excerpt}
        </p>

        <article className="prose-spacing">
          {page.sections.map((section, i) => (
            <Section key={i} section={section} />
          ))}
        </article>

        {page.faqs.length > 0 && (
          <section className="mt-12 pt-10 border-t border-border/60">
            <h2 className="text-2xl font-bold mb-6">Frequently asked questions</h2>
            <div className="space-y-6">
              {page.faqs.map((faq, i) => (
                <div key={i} className="border border-border rounded-xl p-6">
                  <h3 className="font-semibold text-lg mb-2 leading-snug">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {page.relatedLinks && page.relatedLinks.length > 0 && (
          <section className="mt-12 pt-10 border-t border-border/60">
            <h2 className="text-xl font-bold mb-2">Related guides</h2>
            <p className="text-sm text-muted-foreground mb-5">Further reading on AI estimating and quoting for UK builders.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {page.relatedLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="group flex items-start gap-3 rounded-xl border border-border p-4 hover:border-primary/40 hover:bg-primary/5 transition-colors"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-1 text-primary group-hover:translate-x-0.5 transition-transform" />
                  <span className="text-sm font-medium leading-snug group-hover:text-primary transition-colors">
                    {link.title}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 pt-8 border-t border-border/60">
          <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-3">Try AI estimating yourself</h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
              Upload drawings or describe the job in plain text. The Proper Tool generates a complete, line-item quote with UK market rates in under 10 minutes.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">Start free, no card needed</Link>
            </Button>
          </div>
        </div>

        <div className="mt-8 text-center">
          <Link href="/blog" className="text-sm text-primary hover:underline flex items-center gap-1 justify-center">
            <ChevronRight className="h-4 w-4 rotate-180" />
            Back to Blog &amp; Guides
          </Link>
        </div>
      </main>

      <footer className="border-t border-border/40 mt-16 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} The Proper Tool. Built for UK builders.</p>
          <div className="flex gap-6">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
